/**
 * THE AFTER-ACTION REPORT — 3.19 Learning & Governance spine, A1 (pure layer).
 *
 * The retrospective a project writes when it closes: what it set out to do, what
 * it delivered, what moved and why, and what to sustain or improve next time. It
 * is the third leg of the spine — the Decisions log (D1) records the calls made
 * on the way, the Lessons register (L1) carries the generalised insight forward,
 * and the AAR is where a closing project turns one into the other.
 *
 * WHY IT IS A DOCUMENT, NOT A NEW OBJECT. An AAR is produced, filed, versioned,
 * signed and exported to PDF exactly like a change request or a charter, so it
 * rides the Documents machinery whole: `kind: 'aar'` (migration 0174).
 *
 * WHY ITS CONTENT IS RICH TEXT. A closing PM formats the retrospective with
 * headings, bold and lists — so, unlike a change request's plain narrative
 * strings, the AAR's four sections are ProseMirror RichDoc, edited in the same
 * RichTextEditor the charter uses and stored on the `aar` jsonb column (AarContent
 * in types.ts). `composeAar` PRE-FILLS the first three from what the board already
 * knows (as real paragraphs and a bullet list of the decisions), so a closing PM
 * is editing a draft, not facing an empty page; the fourth, sustain-and-improve,
 * is left for the person, because what to keep doing is the one thing the board
 * cannot infer. Each line a person writes there becomes a Lesson through
 * `lessonFromAarLine`, which is how the AAR feeds the register the next project
 * reads.
 *
 * WHY IT IS PURE FUNCTIONS AND NOT A SCREEN. Like `change-request.ts`, everything
 * here is "given the board, what does the report say" — the part that has to be
 * right and the part a browser makes slow to check. The editor renders what these
 * return.
 */
import type { Doc, Issue, Build, AarContent, AarSustainLine, AarVariance, AarBenefitLine, AarBenefitStatus } from './types';
import type { Project } from './projects';
import type { Decision } from './decisions';
import { liveDecisions } from './decisions';
import type { Lesson, LessonCategory, LessonDraft, AarLineKind } from './lessons';
import { type RichDoc, richText, richTopBlocks } from './rich-text';
import { fmtMoney } from './money';
import { humanDay } from './when';

/** The sections, in the order they are written and printed. */
export type AarSectionKey = 'supposed' | 'actual' | 'benefits' | 'why' | 'sustain';

export interface AarSection {
  key: AarSectionKey;
  heading: string;
  /** Shown under the heading in the editor — what belongs in the section. */
  help: string;
  /** Whether the board pre-fills this section (the person edits) or writes it. */
  prefilled: boolean;
}

export const AAR_SECTIONS: AarSection[] = [
  {
    key: 'supposed',
    heading: 'What we set out to do',
    help: 'The objectives and scope agreed at the start, and the dates and budget we committed to.',
    prefilled: true,
  },
  {
    key: 'actual',
    heading: 'What we delivered',
    help: 'What was completed and when it went live, measured against what was planned.',
    prefilled: true,
  },
  {
    key: 'benefits',
    heading: 'What benefits we expected, and where they stand',
    help: 'The value the project was for — not what shipped, but whether it paid off. Seeded from the charter; set where each benefit stands and who tracks it after close.',
    prefilled: true,
  },
  {
    key: 'why',
    heading: 'What moved, and why',
    help: 'Where the plan changed — slippage, blockers cleared, changes agreed, and the decisions behind them.',
    prefilled: true,
  },
  {
    key: 'sustain',
    heading: 'What to sustain and improve',
    help: 'What to keep doing and what to change next time. Save each line to the Lessons register so the next project starts from it.',
    prefilled: false,
  },
];

/** A section's heading, by key — the single source the editor AND the produced PDF
 *  read, so a reworded section can never diverge between the screen and the file. */
export const aarHeading = (key: AarSectionKey): string =>
  AAR_SECTIONS.find((s) => s.key === key)?.heading ?? '';

/** The empty-state line for the "sustain and improve" section, shown on screen and
 *  printed in the PDF — one phrase in one place (R13: two surfaces, the same words). */
export const AAR_SUSTAIN_EMPTY = 'Nothing recorded yet.';

// ── RichDoc builders ─────────────────────────────────────────────────────────
// Small helpers that assemble the ProseMirror nodes the RichTextEditor reads, so
// the composed sections arrive as real paragraphs and lists, not a wall of text.

const para = (text?: string): RichDoc =>
  text ? { type: 'paragraph', content: [{ type: 'text', text }] } : { type: 'paragraph' };

const bulletList = (items: string[]): RichDoc => ({
  type: 'bulletList',
  content: items.map((t) => ({ type: 'listItem', content: [para(t)] })),
});

/** A whole document from block nodes — never empty, so the editor always mounts. */
const doc = (blocks: RichDoc[]): RichDoc => ({
  type: 'doc',
  content: blocks.length ? blocks : [para()],
});

/**
 * WHAT THE REPORT IS COMPOSED FROM — the board facts `composeAar` reads. The
 * caller (the store) gathers these from the live project; `aarInputsFromState`
 * does that mapping. Everything optional is genuinely optional — a project with
 * no charter, no budget and no go-live still composes a useful report from its
 * cards alone.
 */
export interface AarInputs {
  projectName: string;
  /** The charter's objectives/purpose, as plain text. */
  objectives?: string;
  /** The charter's objectives & success criteria, as rich text — seeds the benefits. */
  successMeasure?: RichDoc;
  /** What was in scope, as plain text. */
  scope?: string;
  /** The planned go-live, in ms. */
  plannedGoLive?: number;
  /** When it actually went live, in ms (the latest `wentLiveAt` across cards). */
  actualGoLive?: number;
  /** The approved budget baseline. */
  budgetBaseline?: number;
  /** The final forecast cost. */
  forecast?: number;
  issues: Issue[];
  decisions: Decision[];
  /** The project's change requests — title and whether each was signed off. */
  changeRequests: { title: string; signed: boolean }[];
}

/** A `YYYY-MM-DD` day from a ms instant, in local time, for `humanDay`. */
function dayOf(ms: number): string {
  const d = new Date(ms);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** "1 item" / "3 items" — the count with its noun, singular when it is one. */
function plural(n: number, one: string, many = `${one}s`): string {
  return `${n} ${n === 1 ? one : many}`;
}

function composeSupposed(i: AarInputs): RichDoc {
  const blocks: RichDoc[] = [];
  if (i.objectives?.trim()) blocks.push(para(i.objectives.trim()));
  if (i.scope?.trim()) blocks.push(para(`In scope: ${i.scope.trim()}`));
  const commitments: string[] = [];
  if (i.plannedGoLive) commitments.push(`go live by ${humanDay(dayOf(i.plannedGoLive))}`);
  if (i.budgetBaseline != null) commitments.push(`a budget of ${fmtMoney(i.budgetBaseline)}`);
  if (commitments.length) {
    blocks.push(
      para(
        commitments.length === 2
          ? `We committed to ${commitments[0]} within ${commitments[1]}.`
          : `We committed to ${commitments[0]}.`,
      ),
    );
  }
  if (!blocks.length) {
    blocks.push(
      para(
        'The objectives, scope, dates and budget this project set out to meet — fill this in from what was agreed at kickoff.',
      ),
    );
  }
  return doc(blocks);
}

function composeActual(i: AarInputs): RichDoc {
  const total = i.issues.length;
  const delivered = i.issues.filter((x) => x.status === 'done');
  const wontDo = i.issues.filter((x) => x.status === 'wontdo');
  const openAtClose = i.issues.filter((x) => x.status !== 'done' && x.status !== 'wontdo');

  const sentences: string[] = [];
  const tail: string[] = [];
  if (wontDo.length) tail.push(`${wontDo.length} dropped as won’t-do`);
  if (openAtClose.length) tail.push(`${openAtClose.length} still open at close`);
  sentences.push(
    `${delivered.length} of ${plural(total, 'item')} were completed${tail.length ? `, ${tail.join(' and ')}` : ''}.`,
  );

  const musts = i.issues.filter((x) => x.moscow === 'must');
  if (musts.length) {
    const mustsDone = musts.filter((x) => x.status === 'done').length;
    sentences.push(
      `Of ${plural(musts.length, 'must-have item')}, ${mustsDone} ${mustsDone === 1 ? 'was' : 'were'} delivered.`,
    );
  }

  if (i.actualGoLive) {
    let s = `It went live ${humanDay(dayOf(i.actualGoLive))}`;
    if (i.plannedGoLive) {
      const slip = Math.round((i.actualGoLive - i.plannedGoLive) / 86_400_000);
      s +=
        slip > 0
          ? `, ${plural(slip, 'day')} later than planned`
          : slip < 0
            ? `, ${plural(-slip, 'day')} ahead of plan`
            : ', on the planned date';
    }
    sentences.push(`${s}.`);
  }

  if (i.forecast != null && i.budgetBaseline != null) {
    const variance = i.forecast - i.budgetBaseline;
    sentences.push(
      `Final cost came to ${fmtMoney(i.forecast)} against a ${fmtMoney(i.budgetBaseline)} baseline` +
        (variance > 0
          ? ` — ${fmtMoney(variance)} over.`
          : variance < 0
            ? ` — ${fmtMoney(-variance)} under.`
            : ' — on budget.'),
    );
  }

  return doc([para(sentences.join(' '))]);
}

function composeWhy(i: AarInputs): RichDoc {
  const flow: string[] = [];

  const pushed = i.issues.filter((x) => (x.dueDatePushes ?? 0) > 0);
  if (pushed.length) {
    const moves = pushed.reduce((n, x) => n + (x.dueDatePushes ?? 0), 0);
    flow.push(`${plural(pushed.length, 'item')} moved a due date later, ${plural(moves, 'time')} in all.`);
  }

  const cleared = i.issues.filter((x) => !!x.unblockedAt);
  if (cleared.length) {
    flow.push(`${plural(cleared.length, 'blocker')} ${cleared.length === 1 ? 'was' : 'were'} raised and cleared along the way.`);
  }

  if (i.changeRequests.length) {
    const signed = i.changeRequests.filter((c) => c.signed).length;
    flow.push(
      `${plural(i.changeRequests.length, 'change request')} ${i.changeRequests.length === 1 ? 'was' : 'were'} raised${signed ? `, ${signed} signed off` : ''}.`,
    );
  }

  const blocks: RichDoc[] = [];
  if (flow.length) blocks.push(para(flow.join(' ')));

  const live = liveDecisions(i.decisions);
  if (live.length) {
    blocks.push(para('Key decisions recorded:'));
    blocks.push(bulletList(live.slice(0, 8).map((d) => d.title)));
  }

  if (!blocks.length) {
    blocks.push(para('Where the plan changed and why — slippage, blockers, changes and the decisions behind them.'));
  }
  return doc(blocks);
}

/**
 * COMPOSE THE REPORT from the board — the first three sections pre-filled as rich
 * text, the fourth left for the person. Returns an `AarContent` ready to store on
 * the document's `aar`, so the editor opens on a draft rather than a blank page.
 */
export function composeAar(inputs: AarInputs): AarContent {
  return {
    supposed: composeSupposed(inputs),
    actual: composeActual(inputs),
    why: composeWhy(inputs),
    sustain: [],
    variance: aarVariance(inputs),
    benefits: composeBenefits(inputs),
  };
}

/* ── Benefits — the value the project was for, and where it stands ──────────────
 * The closure question PMBOK/PRINCE2 make mandatory: not what shipped (that is
 * "what we delivered") but whether the promised value landed, and who tracks it
 * after close. Decision-tier — the headline, not a benefits ledger. Seeded from the
 * charter's success criteria (one benefit per block), status defaulting to
 * "not yet" (honest: benefits often realise after the project closes). */

export const AAR_BENEFIT_STATUSES: AarBenefitStatus[] = ['realised', 'partial', 'notyet'];
export const AAR_BENEFIT_STATUS_LABELS: Record<AarBenefitStatus, string> = {
  realised: 'Realised',
  partial: 'Partial',
  notyet: 'Not yet',
};
/** The one status-badge shape reused (R3): realised=green, partial=amber, not-yet=neutral. */
export const AAR_BENEFIT_STATUS_RAG: Record<AarBenefitStatus, 'g' | 'a' | 'n'> = {
  realised: 'g',
  partial: 'a',
  notyet: 'n',
};

/** A fresh, empty benefit line — defaults to "not yet", no monitor. */
export function emptyBenefitLine(id: string): AarBenefitLine {
  return { id, statement: { type: 'doc', content: [para()] }, status: 'notyet' };
}

/** Seed the benefits from the charter's success criteria — one per block, each
 *  "not yet" and unmonitored, for the author to set. Empty when there is no charter. */
function composeBenefits(i: AarInputs): AarBenefitLine[] {
  return richTopBlocks(i.successMeasure).map((statement, n) => ({
    id: `b-seed-${n}`,
    statement,
    status: 'notyet' as const,
  }));
}

/* ── Variance — the figures the "why" prompts anchor to ─────────────────────────
 * Snapshotted into the report at compose time (AarVariance), then read by the
 * editor AND the PDF through the SAME line helpers below, so the figure a reason is
 * tied to reads identically on screen and in the file (R13). A prompt is shown only
 * for an ADVERSE variance — later than planned, or over budget — so the box asks
 * only when there is something to explain; on-time / on-or-under shows no prompt. */

/** The variance facts off the composed inputs — absent fields where inputs are. */
export function aarVariance(i: AarInputs): AarVariance {
  const v: AarVariance = {};
  if (i.actualGoLive != null && i.plannedGoLive != null) {
    v.scheduleDays = Math.round((i.actualGoLive - i.plannedGoLive) / 86_400_000);
    v.plannedGoLive = i.plannedGoLive;
    v.actualGoLive = i.actualGoLive;
  }
  if (i.forecast != null && i.budgetBaseline != null) {
    v.costDelta = i.forecast - i.budgetBaseline;
    v.budgetBaseline = i.budgetBaseline;
    v.forecast = i.forecast;
  }
  return v;
}

/** The schedule slip as a one-line figure, or null when on-time / early (no prompt). */
export function scheduleVarianceLine(v?: AarVariance): string | null {
  if (!v || v.scheduleDays == null || v.scheduleDays <= 0) return null;
  return `${plural(v.scheduleDays, 'day')} later than planned`;
}

/** The dates behind the slip — "went live 15 Jan 2026 · planned 1 Jan 2026". */
export function scheduleVarianceContext(v?: AarVariance): string {
  if (!v || v.actualGoLive == null || v.plannedGoLive == null) return '';
  return `went live ${humanDay(dayOf(v.actualGoLive))} · planned ${humanDay(dayOf(v.plannedGoLive))}`;
}

/** The cost overrun as a one-line figure, or null when on-or-under budget (no prompt). */
export function costVarianceLine(v?: AarVariance): string | null {
  if (!v || v.costDelta == null || v.costDelta <= 0) return null;
  return `${fmtMoney(v.costDelta)} over budget`;
}

/** The figures behind the overrun — "final £196,500 · baseline £180,000". */
export function costVarianceContext(v?: AarVariance): string {
  if (!v || v.forecast == null || v.budgetBaseline == null) return '';
  return `final ${fmtMoney(v.forecast)} · baseline ${fmtMoney(v.budgetBaseline)}`;
}

/**
 * THE BOARD FACTS, READ OFF THE STORE STATE — the impure-input mapping kept pure
 * and testable here rather than buried in the store action. The charter's scope is
 * FLATTENED to plain text (a composed sentence, not a nested document); the actual
 * go-live is the latest a card went live; change requests are the `change`
 * documents. Everything the charter does not record degrades to a gentle prompt in
 * the composed section.
 */
export function aarInputsFromState(s: {
  project?: Project;
  issues: Issue[];
  decisions: Decision[];
  documents: Doc[];
  builds: Build[];
}): AarInputs {
  const goneLive = s.builds
    .map((b) => b.wentLiveAt ?? 0)
    .filter((n) => n > 0);
  return {
    projectName: s.project?.name ?? '',
    scope: richText(s.project?.scopeIn) || undefined,
    objectives: richText(s.project?.successMeasure) || undefined,
    successMeasure: s.project?.successMeasure ?? undefined,
    plannedGoLive: s.project?.goLive,
    actualGoLive: goneLive.length ? Math.max(...goneLive) : undefined,
    budgetBaseline: s.project?.budget,
    forecast: s.project?.forecast,
    issues: s.issues,
    decisions: s.decisions,
    changeRequests: s.documents
      .filter((d) => d.kind === 'change')
      .map((d) => ({ title: d.title, signed: d.state === 'signed' })),
  };
}

/**
 * ONE SUSTAIN LINE → A DRAFT LESSON. The AAR's fourth section is where the person
 * writes what to keep doing and what to change; saving a line carries it into the
 * Lessons register so the next project starts from it. A worked/didn't line IS what
 * happened, so its rich text rides as the lesson's `body`; a standalone
 * recommendation line leaves the body for the author and rides as the
 * `recommendation` instead. The line gives a suggested `title`, which the author can
 * edit in the add form it opens pre-filled. The lesson links back to the report.
 */
export function lessonFromAarLine(
  line: AarSustainLine,
  opts: { boardId: string; category?: LessonCategory; doc: Pick<Doc, 'id' | 'title'> },
): LessonDraft {
  const said = richText(line.text).trim();
  const isRec = line.kind === 'recommendation';
  return {
    boardId: opts.boardId,
    // A suggested headline from the line; the author can edit it in the form.
    title: said.slice(0, 80),
    category: opts.category ?? 'governance',
    // A lesson is only ever worked/didn't. A standalone recommendation line
    // becomes a worked lesson carrying the line as its recommendation.
    kind: line.kind === 'didnt' ? 'didnt' : 'worked',
    // worked/didn't → the line IS what happened; a recommendation line leaves the
    // body for the author to complete and rides as the recommendation instead.
    body: isRec ? doc([para()]) : line.text,
    recommendation: isRec ? line.text : undefined,
    links: [{ kind: 'aar', id: opts.doc.id, label: opts.doc.title }],
  };
}

/** A fresh, empty sustain line — a new row in the editor, defaulting to a
 *  recommendation (the most common "what to change next time"). */
export function emptySustainLine(id: string, kind: AarLineKind = 'recommendation'): AarSustainLine {
  return { id, text: { type: 'doc', content: [para()] }, kind };
}

/**
 * THE LESSON ALREADY FILED FROM A SUSTAIN LINE, if any — a live lesson with the
 * same one-line situation that links back to THIS report (docId). The derive-first
 * dedupe, the mirror of the decision log's `alreadyLogged`: it reads the register
 * (not a per-line flag), so a filed line still shows "Saved to lessons" after a
 * reload and a second save never duplicates. An empty line is never filed.
 */
export function filedLessonFromLine(
  lessons: Lesson[],
  line: AarSustainLine,
  docId: string,
): Lesson | undefined {
  const said = richText(line.text).trim();
  if (!said) return undefined;
  return lessons.find(
    (l) =>
      (l.links ?? []).some((k) => k.kind === 'aar' && k.id === docId) &&
      (richText(l.body).trim() === said || richText(l.recommendation).trim() === said),
  );
}
